import { Box, Chip, Divider, ListSubheader, Menu, MenuItem, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useRef, useState } from "react"

import { PermissionActionType } from "@/constants/permissions"
import type { Maybe } from "@/graphql/codegen/graphql"
import { CustomerAccountStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { CUSTOMER_ACCOUNT_STATUS_LABEL_MAP } from "@/screens/Companies/constants/customerAccountDataMap"

interface CustomerAccountStatusMenuProps {
  customerStatus?: Maybe<CustomerAccountStatus>
  onChangeCustomerAccountStatus?: (status: CustomerAccountStatus) => void
}

const CustomerAccountStatusMenu: FC<CustomerAccountStatusMenuProps> = ({
  onChangeCustomerAccountStatus,
  customerStatus,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { hasPermissions } = usePermissionManager()

  const currentCustomerStatus = customerStatus || CustomerAccountStatus.Active

  const anchorElRef = useRef<HTMLDivElement>(null)

  const hasEditPermission = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])
  const hasViewPermission = hasPermissions([PermissionActionType.CUSTOMER_VIEW])

  const handleToggleProductVersion = () => {
    setIsOpen((isOpen) => !isOpen)
  }

  const handleSelectCustomerAccountStatus = (status: CustomerAccountStatus) => {
    handleToggleProductVersion()
    onChangeCustomerAccountStatus?.(status)
  }

  const renderStatusMenuItem = (item: CustomerAccountStatus) => {
    return (
      <MenuItem
        key={"customer_account_status_menu_" + item}
        onClick={() => handleSelectCustomerAccountStatus(item)}
        selected={item === currentCustomerStatus}
      >
        <Box className='flex w-full items-center justify-between'>
          <Typography variant='body2'>{CUSTOMER_ACCOUNT_STATUS_LABEL_MAP[item]}</Typography>
        </Box>
      </MenuItem>
    )
  }

  const ProductVersionChip = (
    <Chip
      avatar={
        <Box className='flex w-fit items-center justify-center rounded bg-gray-500 p-1'>
          <span className='text-[10px] text-gray-900'>Customer Account Status</span>
        </Box>
      }
      label={<span className='px-1'>{CUSTOMER_ACCOUNT_STATUS_LABEL_MAP[currentCustomerStatus]}</span>}
      variant='filled'
      size='small'
      className='rounded bg-gray-100 text-gray-900'
      ref={anchorElRef}
      {...(hasEditPermission && {
        onClick: handleToggleProductVersion,
        deleteIcon: <KeyboardArrowDownOutlined className='text-gray-800' />,
        onDelete: handleToggleProductVersion,
      })}
    />
  )

  return (
    <>
      {hasViewPermission && ProductVersionChip}

      <Menu
        anchorEl={anchorElRef.current}
        open={isOpen}
        onClose={handleToggleProductVersion}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        <ListSubheader className='leading-9'>Customer Account Statuses</ListSubheader>
        <Divider />

        {Object.values(CustomerAccountStatus).map((item) => renderStatusMenuItem(item))}
      </Menu>
    </>
  )
}

export default CustomerAccountStatusMenu
